import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { 
  Search, Ship, Building2, MapPin, Phone, Mail, 
  MoreVertical, Trash2, Power, PowerOff, FileSpreadsheet, 
  Upload, Eye, Check, Clock, AlertCircle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export default function VesselList({ onRefresh }) {
  const [vessels, setVessels] = useState([]);
  const [importedFiles, setImportedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [selectedFileIds, setSelectedFileIds] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [vesselsRes, filesRes, statsRes] = await Promise.all([
        api.get(`/vessels${search ? `?search=${search}` : ''}`),
        api.get("/vessels/imports"),
        api.get("/vessels/stats")
      ]);
      setVessels(vesselsRes.data);
      setImportedFiles(filesRes.data);
      setSelectedFileIds(prev => prev.filter(id => filesRes.data.some(f => f.file_id === id)));
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteVessel = async (vesselId) => {
    if (!window.confirm("Are you sure you want to delete this vessel?")) return;
    
    try {
      await api.delete(`/vessels/${vesselId}`);
      toast.success("Vessel deleted");
      fetchData();
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to delete vessel");
    }
  };

  const handleToggleActive = async (vessel) => {
    try {
      await api.put(`/vessels/${vessel.vessel_id}`, {
        is_active: !vessel.is_active
      });
      toast.success(vessel.is_active ? "Vessel deactivated" : "Vessel activated");
      fetchData();
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to update vessel");
    }
  };

  const handleConfirmImport = async (fileId) => {
    try {
      const response = await api.post(`/vessels/imports/${fileId}/confirm`);
      toast.success(`Imported ${response.data.imported} vessels (${response.data.skipped} skipped)`);
      fetchData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import vessels");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    
    try {
      await api.delete(`/vessels/imports/${fileId}`);
      toast.success("File deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => 
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const handleSelectAllFiles = () => {
    setSelectedFileIds(importedFiles.map(f => f.file_id));
  };

  const handleClearSelection = () => {
    setSelectedFileIds([]);
  };

  const handleDeleteSelectedFiles = async () => {
    if (selectedFileIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedFileIds.length} selected files?`)) return;
    
    try {
      const response = await api.post("/vessels/imports/bulk-delete", { file_ids: selectedFileIds });
      toast.success(`Deleted ${response.data.deleted} files`);
      setSelectedFileIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete files");
    }
  };

  // Calculate total emails from imported files
  const totalEmails = importedFiles.reduce((acc, f) => acc + (f.total_emails || 0), 0);

  // Filter vessels to show only manually added ones (not from imports)
  const manualVessels = vessels.filter(v => !v.imported_from);

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 text-amber-500">
            <Clock className="h-4 w-4" />
            <span>Pending</span>
          </span>
        );
      case "imported":
        return (
          <span className="inline-flex items-center gap-1 text-emerald-500">
            <Check className="h-4 w-4" />
            <span>Imported</span>
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1 text-red-500">
            <AlertCircle className="h-4 w-4" />
            <span>Failed</span>
          </span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards - Matching the reference design */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-card border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Ship className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Vessels</p>
                <p className="text-3xl font-bold text-foreground">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-500/20">
                <FileSpreadsheet className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Imported Files</p>
                <p className="text-3xl font-bold text-foreground">{importedFiles.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-violet-500/20">
                <Upload className="h-6 w-6 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Emails</p>
                <p className="text-3xl font-bold text-foreground">{totalEmails}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Imported Files Section - Shows ONE entry per file, NOT individual emails */}
      <Card className="bg-card border shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">Imported Files</CardTitle>
              <p className="text-sm text-muted-foreground">
                Files show as single entries with email count
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={selectedFileIds.length === importedFiles.length ? handleClearSelection : handleSelectAllFiles}
                disabled={importedFiles.length === 0}
              >
                {selectedFileIds.length === importedFiles.length ? "Clear Selection" : "Select All"}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeleteSelectedFiles}
                disabled={selectedFileIds.length === 0}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {importedFiles.length === 0 ? (
            <div className="text-center py-8">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No imported files yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Use the Import button to upload CSV or Excel files
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedFileIds.length === importedFiles.length && importedFiles.length > 0}
                        onChange={(e) => (e.target.checked ? handleSelectAllFiles() : handleClearSelection())}
                        className="h-4 w-4"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Total Emails
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Imported Date
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {importedFiles.map((file) => (
                    <tr 
                      key={file.file_id} 
                      className="border-b border-border/50 hover:bg-muted/30"
                      data-testid={`imported-file-row-${file.file_id}`}
                    >
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={selectedFileIds.includes(file.file_id)}
                          onChange={() => toggleFileSelection(file.file_id)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                          <span className="font-medium">{file.file_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-muted-foreground">{file.total_emails} emails</span>
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(file.status)}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {new Date(file.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {file.status === "pending" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-primary"
                              onClick={() => handleConfirmImport(file.file_id)}
                              title="Confirm Import"
                              data-testid={`confirm-import-${file.file_id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-500 hover:text-blue-600"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteFile(file.file_id)}
                            title="Delete File"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual Vessels Section - Manually added vessels */}
      <Card className="bg-card border shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">Individual Vessels</CardTitle>
              <p className="text-sm text-muted-foreground">
                Manually added vessels
              </p>
            </div>
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vessels..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="vessel-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {manualVessels.length === 0 ? (
            <div className="text-center py-12">
              <Ship className="h-16 w-16 text-muted-foreground/20 mx-auto mb-4" />
              <p className="text-muted-foreground">No vessels found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {search 
                  ? "Try adjusting your search" 
                  : "Click 'Add Vessel' to add your first vessel manually"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Vessel
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Details
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {manualVessels.map((vessel) => (
                    <tr 
                      key={vessel.vessel_id}
                      className="border-b border-border/50 hover:bg-muted/30"
                      data-testid={`vessel-row-${vessel.vessel_id}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded bg-primary/10">
                            <Ship className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">{vessel.name}</div>
                            {vessel.company && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Building2 className="h-3 w-3" />
                                {vessel.company}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            {vessel.email}
                          </div>
                          {vessel.phone && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {vessel.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          {vessel.vessel_type && (
                            <div className="text-sm">{vessel.vessel_type}</div>
                          )}
                          {vessel.port && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {vessel.port}
                            </div>
                          )}
                          {vessel.imo_number && (
                            <div className="text-xs font-mono text-muted-foreground">
                              IMO: {vessel.imo_number}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={vessel.is_active ? "badge-sent" : "badge-cancelled"}>
                          {vessel.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`vessel-menu-${vessel.vessel_id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleToggleActive(vessel)}>
                                {vessel.is_active ? (
                                  <>
                                    <PowerOff className="h-4 w-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <Power className="h-4 w-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteVessel(vessel.vessel_id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
